import { openCompositionDatabase } from './storage';

/**
 * Retained original recordings. These stay on the device that captured them: the
 * composition document carries only a reference, so sync moves the score without
 * silently uploading a musician's room audio.
 */
export type TakeAudioAsset = {
  ownerId: string;
  id: string;
  compositionId: string;
  takeId: string;
  mimeType: string;
  byteLength: number;
  durationMs: number;
  sampleRate?: number;
  createdAt: string;
  blob: Blob;
};

export type TakeAudioUsage = { byteLength: number; count: number };

/** One take may not exceed this; a longer improvisation is truncated by the caller. */
export const MAX_TAKE_AUDIO_BYTES = 48 * 1024 * 1024;
/** Total retained audio per account on this device. */
export const MAX_OWNER_AUDIO_BYTES = 250 * 1024 * 1024;

export class TakeAudioQuotaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TakeAudioQuotaError';
  }
}

const readTransaction = <T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> =>
  new Promise((resolve, reject) => {
    const transaction = db.transaction('assets', mode);
    const request = run(transaction.objectStore('assets'));
    transaction.oncomplete = () => resolve(request.result);
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('Recorded audio storage is unavailable.'));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('Recorded audio storage was interrupted.'));
  });

export async function getTakeAudioUsage(ownerId: string): Promise<TakeAudioUsage> {
  const db = await openCompositionDatabase();
  try {
    const assets = await readTransaction<TakeAudioAsset[]>(db, 'readonly', (store) =>
      store.index('owner').getAll(ownerId),
    );
    return {
      byteLength: assets.reduce((total, asset) => total + (asset.byteLength ?? 0), 0),
      count: assets.length,
    };
  } finally {
    db.close();
  }
}

/**
 * Stores one take's recording. Quota is checked against what is already retained so a
 * full device reports an honest failure instead of a take that silently lost its audio.
 */
export async function putTakeAudio(
  asset: Omit<TakeAudioAsset, 'createdAt'> & { createdAt?: string },
): Promise<TakeAudioAsset> {
  if (asset.byteLength > MAX_TAKE_AUDIO_BYTES)
    throw new TakeAudioQuotaError(
      'This recording is too long to keep on the device. The notes were saved without the original audio.',
    );
  const usage = await getTakeAudioUsage(asset.ownerId);
  if (usage.byteLength + asset.byteLength > MAX_OWNER_AUDIO_BYTES)
    throw new TakeAudioQuotaError(
      'Recorded audio storage is full. Delete an older take to keep new recordings.',
    );
  const record: TakeAudioAsset = { createdAt: new Date().toISOString(), ...asset };
  const db = await openCompositionDatabase();
  try {
    await readTransaction(db, 'readwrite', (store) => store.put(record));
    return record;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError')
      throw new TakeAudioQuotaError(
        'This device is out of storage. The notes were saved without the original audio.',
      );
    throw error;
  } finally {
    db.close();
  }
}

export async function getTakeAudio(
  ownerId: string,
  assetId: string,
): Promise<TakeAudioAsset | undefined> {
  const db = await openCompositionDatabase();
  try {
    return await readTransaction<TakeAudioAsset | undefined>(db, 'readonly', (store) =>
      store.get([ownerId, assetId]),
    );
  } finally {
    db.close();
  }
}

export async function deleteTakeAudio(ownerId: string, assetId: string): Promise<void> {
  const db = await openCompositionDatabase();
  try {
    await readTransaction(db, 'readwrite', (store) => store.delete([ownerId, assetId]));
  } finally {
    db.close();
  }
}

/**
 * Removes recordings whose take no longer exists in the composition. Deleting a
 * sketch or a take must not leave its audio occupying the device indefinitely.
 */
export async function pruneTakeAudio(
  ownerId: string,
  compositionId: string,
  keepAssetIds: string[],
): Promise<number> {
  const keep = new Set(keepAssetIds);
  const db = await openCompositionDatabase();
  try {
    const assets = await readTransaction<TakeAudioAsset[]>(db, 'readonly', (store) =>
      store.index('composition').getAll([ownerId, compositionId]),
    );
    const stale = assets.filter((asset) => !keep.has(asset.id));
    for (const asset of stale)
      await readTransaction(db, 'readwrite', (store) => store.delete([asset.ownerId, asset.id]));
    return stale.length;
  } finally {
    db.close();
  }
}
